import React, { useState, useEffect, useRef, useMemo } from 'react';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, getFileUrl, subBrandAPI } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmProvider';

// Products Tab Component
export const ProductsTab = ({ products, fetchData, dialogOpen, setDialogOpen }) => {
  const confirm = useConfirm();
  const [formData, setFormData] = useState({ name: '', description: '', price: '', unit: 'quintal', stock: '', discount_percent: '10', image_url: '', batch_size_e302: '', batch_size_e303: '' });
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [bulkDiscount, setBulkDiscount] = useState('10');
  const [applyingBulk, setApplyingBulk] = useState(false);

  // Sub-brands master list (used by MISC line items in Orders)
  const [subBrands, setSubBrands] = useState([]);
  const [newSubBrand, setNewSubBrand] = useState('');
  const [savingSubBrand, setSavingSubBrand] = useState(false);

  const fetchSubBrands = async () => {
    try {
      const res = await subBrandAPI.getAll();
      setSubBrands(res.data || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchSubBrands(); }, []);

  const addSubBrand = async () => {
    const name = newSubBrand.trim();
    if (!name) { toast.error('Enter a sub-brand name'); return; }
    setSavingSubBrand(true);
    try {
      await subBrandAPI.create(name);
      toast.success(`Sub-brand "${name}" added`);
      setNewSubBrand('');
      fetchSubBrands();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add sub-brand');
    } finally {
      setSavingSubBrand(false);
    }
  };

  const removeSubBrand = async (sb) => {
    if (!(await confirm({ title: `Delete sub-brand "${sb.name}"?`, description: 'This only removes the tag — past orders that used it remain unchanged.', destructive: true }))) return;
    try {
      await subBrandAPI.delete(sb.id);
      toast.success('Sub-brand removed');
      fetchSubBrands();
    } catch (e) {
      toast.error('Failed to remove sub-brand');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = formData.image_url;

      if (imageFile) {
        setUploading(true);
        const uploadRes = await productAPI.uploadImage(imageFile);
        imageUrl = uploadRes.data.path;
        setUploading(false);
      }

      const productData = {
        ...formData,
        image_url: imageUrl,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        discount_percent: formData.discount_percent === '' ? 10 : parseFloat(formData.discount_percent),
        batch_size_e302: formData.batch_size_e302 === '' || formData.batch_size_e302 == null ? null : parseInt(formData.batch_size_e302),
        batch_size_e303: formData.batch_size_e303 === '' || formData.batch_size_e303 == null ? null : parseInt(formData.batch_size_e303),
      };

      if (editingId) {
        await productAPI.update(editingId, productData);
        toast.success('Product updated');
      } else {
        await productAPI.create(productData);
        toast.success('Product created');
      }
      setDialogOpen(false);
      setFormData({ name: '', description: '', price: '', unit: 'quintal', stock: '', discount_percent: '10', image_url: '', batch_size_e302: '', batch_size_e303: '' });
      setEditingId(null);
      setImageFile(null);
      setImagePreview(null);
      fetchData();
    } catch (error) {
      setUploading(false);
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price ?? '',
      unit: product.unit || 'quintal',
      stock: product.stock ?? '',
      discount_percent: product.discount_percent ?? 10,
      image_url: product.image_url || '',
      batch_size_e302: product.batch_size_e302 ?? '',
      batch_size_e303: product.batch_size_e303 ?? '',
    });
    setEditingId(product.id);
    setImageFile(null);
    setImagePreview(null);
    setDialogOpen(true);
  };

  const handleApplyBulkDiscount = async () => {
    const pct = parseFloat(bulkDiscount);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Enter a discount between 0 and 100');
      return;
    }
    if (!(await confirm({
      title: `Apply ${pct}% discount to ALL products?`,
      description: `This overwrites the current per-product discount on all ${products.length} products.`,
      confirmLabel: 'Apply',
      destructive: true,
    }))) return;
    setApplyingBulk(true);
    try {
      const res = await productAPI.setDefaultDiscount(pct);
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error('Failed to apply bulk discount');
    } finally {
      setApplyingBulk(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({
      title: 'Delete this product?',
      description: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    }))) return;
    try {
      await productAPI.delete(id);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const getImageSrc = (product) => {
    if (!product.image_url) return null;
    if (product.image_url.startsWith('http')) return product.image_url;
    return getFileUrl(product.image_url);
  };

  return (
    <div data-testid="products-section">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 md:mb-8">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#2B5339' }}>Products</h2>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1 px-2 py-1 rounded border" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }} title="Sets the same discount on every product">
            <span className="text-xs" style={{ color: '#5C6B61' }}>Set default for all:</span>
            <Input
              type="number" min="0" max="100" step="0.01"
              value={bulkDiscount}
              onChange={(e) => setBulkDiscount(e.target.value)}
              className="h-8 w-16 text-sm"
              data-testid="bulk-discount-input"
            />
            <span className="text-xs" style={{ color: '#5C6B61' }}>%</span>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={handleApplyBulkDiscount}
              disabled={applyingBulk}
              data-testid="apply-bulk-discount-btn"
            >
              {applyingBulk ? 'Applying...' : 'Apply'}
            </Button>
          </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button style={{ background: '#2B5339' }} data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="product-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="product-name-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required data-testid="product-description-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Price (₹)</label>
                    <Input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required data-testid="product-price-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Unit</label>
                    <Input value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} required data-testid="product-unit-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Stock</label>
                    <Input type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} required data-testid="product-stock-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Default Discount %</label>
                    <Input type="number" min="0" max="100" step="0.01" value={formData.discount_percent} onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })} placeholder="10" data-testid="product-discount-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Batch size E-302 <span className="text-xs" style={{ color: '#5C6B61' }}>(bags)</span></label>
                    <Input type="number" min="1" value={formData.batch_size_e302} onChange={(e) => setFormData({ ...formData, batch_size_e302: e.target.value })} placeholder="leave blank if N/A" data-testid="product-batch-e302-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Batch size E-303 <span className="text-xs" style={{ color: '#5C6B61' }}>(bags)</span></label>
                    <Input type="number" min="1" value={formData.batch_size_e303} onChange={(e) => setFormData({ ...formData, batch_size_e303: e.target.value })} placeholder="leave blank if N/A" data-testid="product-batch-e303-input" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Product Image</label>
                  <div className="mt-1">
                    {(imagePreview || formData.image_url) && (
                      <img
                        src={imagePreview || (formData.image_url?.startsWith('http') ? formData.image_url : getFileUrl(formData.image_url))}
                        alt="Preview"
                        className="w-full h-32 object-cover rounded mb-2"
                        data-testid="product-image-preview"
                      />
                    )}
                    <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded cursor-pointer hover:bg-[#FBF9F6] transition-colors" style={{ borderColor: '#E2DFD8' }}>
                      <Upload className="w-5 h-5" style={{ color: '#5C6B61' }} />
                      <span className="text-sm" style={{ color: '#5C6B61' }}>{imageFile ? imageFile.name : 'Choose image file'}</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleImageSelect}
                        className="hidden"
                        data-testid="product-image-upload"
                      />
                    </label>
                    {uploading && <p className="text-xs mt-1" style={{ color: '#C05C35' }}>Uploading image...</p>}
                  </div>
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button type="submit" style={{ background: '#2B5339' }} data-testid="save-product-btn">
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Sub-Brands master list — used when admin adds a MISC line item on an order */}
      <Card className="p-4 mb-6 bg-white border" style={{ borderColor: '#E2DFD8' }} data-testid="sub-brands-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-bold" style={{ color: '#2B5339' }}>Sub-Brands</h3>
            <p className="text-xs mt-0.5" style={{ color: '#5C6B61' }}>
              Tag list for MISC line items in New Order / Edit Order. Each MISC entry on an order picks one sub-brand + the parent product it stacks under.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <Input
            type="text"
            placeholder="e.g. Premium, Lite, Special"
            value={newSubBrand}
            onChange={(e) => setNewSubBrand(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubBrand(); } }}
            className="h-9 max-w-xs"
            data-testid="new-sub-brand-input"
          />
          <Button
            type="button"
            size="sm"
            onClick={addSubBrand}
            disabled={savingSubBrand}
            style={{ background: '#2B5339' }}
            data-testid="add-sub-brand-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> {savingSubBrand ? 'Adding…' : 'Add'}
          </Button>
        </div>
        {subBrands.length === 0 ? (
          <p className="text-xs italic" style={{ color: '#5C6B61' }}>No sub-brands yet. Add the first one above.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {subBrands.map((sb) => (
              <span
                key={sb.id}
                className="inline-flex items-center gap-1 text-xs font-medium pl-2.5 pr-1 py-1 rounded-full border"
                style={{ borderColor: '#E2DFD8', background: '#FBF9F6', color: '#2B5339' }}
                data-testid="sub-brand-chip"
              >
                {sb.name}
                <button
                  type="button"
                  onClick={() => removeSubBrand(sb)}
                  className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-[#F3E0D9]"
                  style={{ color: '#C05C35' }}
                  title="Delete sub-brand"
                  aria-label={`Delete ${sb.name}`}
                  data-testid="remove-sub-brand-btn"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <Card key={product.id} className="p-6 bg-white border" style={{ borderColor: '#E2DFD8' }} data-testid="admin-product-card">
            {getImageSrc(product) && (
              <img src={getImageSrc(product)} alt={product.name} className="w-full h-40 object-cover rounded mb-4" />
            )}
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1E2420' }}>{product.name}</h3>
            <p className="text-sm mb-4" style={{ color: '#5C6B61' }}>{product.description}</p>
            <div className="mb-4">
              <p className="text-2xl font-bold" style={{ color: '#2B5339' }}>₹{product.price}</p>
              <p className="text-sm" style={{ color: '#5C6B61' }}>per {product.unit}</p>
            </div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm" style={{ color: '#5C6B61' }}>Stock: {product.stock}</p>
              <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: '#EFF4EE', color: '#2B5339' }} data-testid="product-disc-badge">
                Disc: {product.discount_percent ?? 10}%
              </span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleEdit(product)} data-testid="edit-product-btn">
                <Edit className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(product.id)} data-testid="delete-product-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

